import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Activity } from "lucide-react";
import { formatApiError } from "@/lib/api";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created. Complete UPI payment to unlock access.");
      nav("/paywall", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <div className="flex items-center gap-2 mb-8">
            <Activity className="h-5 w-5 text-blue-900" strokeWidth={1.75} />
            <span className="font-heading font-extrabold">RIF Master</span>
          </div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Create your account</h1>
          <p className="mt-2 text-sm text-slate-600">One-time UPI payment unlocks lifetime access.</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="name">Full name</Label>
              <Input id="name" required minLength={2} autoComplete="name"
                value={name} onChange={(e) => setName(e.target.value)}
                data-testid="register-name-input" />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required autoComplete="email"
                value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="register-email-input" />
            </div>
            <div>
              <Label htmlFor="password">Password (min 6 chars)</Label>
              <Input id="password" type="password" required minLength={6} autoComplete="new-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="register-password-input" />
            </div>
            <Button type="submit" disabled={loading} className="w-full h-11 bg-blue-900 hover:bg-blue-800" data-testid="register-submit-btn">
              {loading ? "Creating account…" : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-sm text-slate-600">
            Already registered? <Link to="/login" className="text-blue-900 font-semibold hover:underline" data-testid="link-to-login">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative bg-blue-900">
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative h-full p-12 flex flex-col justify-end text-white">
          <div className="eyebrow text-blue-200">Trusted by fertility specialists</div>
          <p className="mt-3 text-3xl font-heading font-extrabold tracking-tight leading-tight max-w-md">
            The most comprehensive RIF protocol tool — dosed to the last milligram.
          </p>
          <p className="mt-4 text-sm text-blue-100 max-w-md">
            Includes ERA-directed FET timing, CD138 endometritis regimens, WHO Cat-I ATT for endometrial TB, DSA/HLA cross-match immunosuppression and KIR–HLA-C mismatch G-CSF pathway.
          </p>
        </div>
      </div>
    </div>
  );
}
